import { createServerClient as createServerSupabaseClient, createBrowserClient as createBrowserSupabaseClient } from '@supabase/ssr'
import { createClient as createSupabaseClient, SupabaseClient, AuthChangeEvent } from '@supabase/supabase-js'
import type { Database } from './types'
import { sanitizeSupabaseCookies } from './cookie-utils'
import {
  authCookieDomainFor,
  browserAuthCookieHandlers,
  clearBrowserAuthCookies,
  splitAuthCookieWrites,
} from './auth-cookie-scope'

// Trim env vars to remove accidental trailing newlines/whitespace
// This prevents WebSocket auth failures from malformed API keys
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim() || ''
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim() || ''

// =============================================================================
// Auth Refresh Circuit Breaker
// =============================================================================
// Prevents infinite refresh token loops when signInWithIdToken returns an
// invalid 12-char refresh token (known issue with manual Google OAuth flow).
// =============================================================================

interface RefreshCircuitBreaker {
  failureCount: number
  lastFailureTime: number
  isOpen: boolean
}

const CIRCUIT_BREAKER_MAX_FAILURES = 3
const CIRCUIT_BREAKER_RESET_MS = 30000 // 30 seconds
const circuitBreaker: RefreshCircuitBreaker = {
  failureCount: 0,
  lastFailureTime: 0,
  isOpen: false,
}

/**
 * Records a refresh token failure and potentially opens the circuit breaker.
 * When open, no more refresh attempts will be made until the breaker resets.
 */
function recordRefreshFailure(): boolean {
  const now = Date.now()

  // Reset failure count if enough time has passed
  if (now - circuitBreaker.lastFailureTime > CIRCUIT_BREAKER_RESET_MS) {
    circuitBreaker.failureCount = 0
    circuitBreaker.isOpen = false
  }

  circuitBreaker.failureCount++
  circuitBreaker.lastFailureTime = now

  if (circuitBreaker.failureCount >= CIRCUIT_BREAKER_MAX_FAILURES) {
    circuitBreaker.isOpen = true
    console.warn(
      '[Auth Circuit Breaker] Opened after',
      circuitBreaker.failureCount,
      'refresh failures. Will reset in',
      CIRCUIT_BREAKER_RESET_MS / 1000,
      'seconds.'
    )
    return true
  }

  return false
}

/**
 * Resets the circuit breaker on successful auth operations.
 */
function resetCircuitBreaker() {
  if (circuitBreaker.failureCount > 0) {
    circuitBreaker.failureCount = 0
    circuitBreaker.isOpen = false
  }
}

/**
 * Clears all Supabase auth cookies (including chunked ones).
 * Called when circuit breaker opens to prevent further refresh attempts.
 * Also exported for manual logout scenarios.
 */
export function clearSupabaseAuthCookies() {
  if (typeof document === 'undefined') return

  // Both scopes, via the single scope rule (auth-cookie-scope.ts).
  clearBrowserAuthCookies()
}

// =============================================================================
// Browser Client (for client-side React components)
// =============================================================================

// Singleton to prevent "Multiple GoTrueClient instances" warning
// which causes AbortError when competing auth operations run concurrently
let browserClient: SupabaseClient<Database> | null = null

/**
 * Creates a Supabase client for browser-side usage.
 *
 * Uses @supabase/ssr's createBrowserClient which reads sessions from cookies
 * (including chunked cookies). This is required for cross-subdomain auth where
 * sessions are stored in cookies set by app.amtil.co but accessed from
 * connect.amtil.co, courses.amtil.co, etc.
 *
 * Includes a circuit breaker to prevent infinite refresh token loops when
 * the refresh token is invalid (known issue with signInWithIdToken).
 *
 * NOTE: @supabase/ssr forces detectSessionInUrl: true which may cause AbortErrors
 * on pages with manual token handling. For those pages, use createBrowserClientSimple().
 */
export function createBrowserClient() {
  if (browserClient) {
    return browserClient
  }

  // Use @supabase/ssr's createBrowserClient for proper cookie-based session handling
  // This correctly reads chunked auth cookies (sb-xxx-auth-token.0, .1, etc.)
  // Type cast needed due to minor type differences between @supabase/ssr and @supabase/supabase-js
  //
  // Cookie SCOPE (BUG-4e4b119c): the login callbacks write Domain=.amtil.co; the
  // ssr default (no domain) made every browser-side refresh create a host-only
  // twin that later shadowed a fresh login. Write the shared scope + delete the
  // host-only twin (auth-cookie-scope.ts).
  const cookieDomain = typeof window !== 'undefined' ? authCookieDomainFor(window.location.hostname) : undefined
  const client = createBrowserSupabaseClient<Database>(
    SUPABASE_URL,
    SUPABASE_ANON_KEY,
    { cookies: browserAuthCookieHandlers(cookieDomain) }
  ) as unknown as SupabaseClient<Database>

  // Set up auth state change listener to detect refresh failures
  // and implement circuit breaker pattern
  client.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
    // Reset circuit breaker on successful auth events
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      resetCircuitBreaker()
      return
    }

    // Detect refresh token failures
    // When signInWithIdToken returns invalid 12-char refresh token,
    // subsequent refresh attempts fail with SIGNED_OUT event
    if (event === 'SIGNED_OUT' && !session) {
      // Check if this is likely a refresh failure (not intentional sign out)
      // by looking at whether we had a session before
      const shouldTriggerCircuitBreaker = recordRefreshFailure()

      if (shouldTriggerCircuitBreaker) {
        // Clear cookies to stop the refresh loop
        clearSupabaseAuthCookies()

        // Redirect to login if we're not already there
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          console.warn('[Auth Circuit Breaker] Redirecting to login due to auth failure loop')
          // Use app.amtil.co/login for cross-subdomain auth
          window.location.href = `https://app.amtil.co/login?redirect=${encodeURIComponent(window.location.href)}`
        }
      }
    }
  })

  browserClient = client
  return browserClient
}

// Separate singleton for simple client
let browserClientSimple: SupabaseClient<Database> | null = null

/**
 * Creates a simpler Supabase client that uses localStorage for session storage.
 *
 * Use this ONLY on pages with manual OAuth token handling (onboarding pages)
 * where detectSessionInUrl: true causes AbortErrors. For most pages, use
 * createBrowserClient() instead.
 *
 * WARNING: This client cannot read cross-subdomain cookie sessions.
 */
export function createBrowserClientSimple() {
  if (browserClientSimple) {
    return browserClientSimple
  }

  browserClientSimple = createSupabaseClient<Database>(
    SUPABASE_URL,
    SUPABASE_ANON_KEY,
    {
      auth: {
        autoRefreshToken: false,
        persistSession: true,
        detectSessionInUrl: false,
      },
    }
  )

  return browserClientSimple
}

// =============================================================================
// Server Client (for Next.js server components and API routes)
// =============================================================================

// Cookie store interface - compatible with Next.js cookies() and similar APIs
interface CookieStore {
  getAll?: () => Array<{ name: string; value: string }>
  get?: (name: string) => { name?: string; value: string } | undefined
  set?: (name: string, value: string, options?: object) => void
  setAll?: (cookies: Array<{ name: string; value: string; options?: object }>) => void
  remove?: (name: string, options?: object) => void
}

/**
 * @param options.hostname the REQUEST host — `(await headers()).get('host')` or
 *   `requestHostname(request)` — so a refresh lands on the same Domain=.amtil.co
 *   cookie the login wrote. REQUIRED (a null/undefined value keeps writes
 *   host-only for localhost/previews): omitting it silently recreated the
 *   dual-scope login loop (BUG-4e4b119c), so the type makes that a compile error.
 */
export function createServerClient(cookieStore: CookieStore, options: { hostname: string | null | undefined }) {
  const cookieDomain = authCookieDomainFor(options.hostname)
  return createServerSupabaseClient<Database>(
    SUPABASE_URL,
    SUPABASE_ANON_KEY,
    {
      cookies: {
        getAll() {
          if (typeof cookieStore.getAll !== 'function') {
            return []
          }
          const { cookies: safeCookies } = sanitizeSupabaseCookies(cookieStore.getAll())
          return safeCookies
        },
        setAll(cookiesToSet: { name: string; value: string; options: Record<string, unknown> }[]) {
          const setCookie = cookieStore.set
          if (typeof setCookie !== 'function') {
            return
          }
          // Domain-scoped writes only: next/headers cookies() is keyed by name, so a
          // host-only delete for the same name would just be replaced by the write.
          // Middleware purges host-only twins on the next verified navigation.
          splitAuthCookieWrites(cookiesToSet, cookieDomain).sets.forEach(({ name, value, options }) => {
            try {
              setCookie(name, value, options)
            } catch {
              // Server Component - can't set cookies
            }
          })
        },
      },
    }
  )
}

// =============================================================================
// Admin Client (for server-side operations with service role key)
// =============================================================================

export function createAdminClient() {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY

  if (!supabaseUrl || !serviceRoleKey) {
    throw new Error('Missing Supabase admin credentials')
  }

  return createSupabaseClient<Database>(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  })
}

// =============================================================================
// Edge Function Client (for Supabase Edge Functions)
// =============================================================================

export function createEdgeClient(authHeader?: string) {
  const supabaseUrl = process.env.SUPABASE_URL
  const anonKey = process.env.SUPABASE_ANON_KEY

  if (!supabaseUrl || !anonKey) {
    throw new Error('Missing Supabase credentials')
  }

  return createSupabaseClient<Database>(supabaseUrl, anonKey, {
    global: {
      headers: authHeader ? { Authorization: authHeader } : {},
    },
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  })
}
